// src/components/Dashboard/Dashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fieldService, imageService, messageService, getImageUrl } from '../../services/api';
import './Dashboard.css';
import FieldMap from '../Map/FieldMap';
import NdviTimeline from '../Charts/NdviTimeline';
import WeatherWidget from './WeatherWidget';
import SensorDetail from './SensorDetail';
import UserManagement from './UserManagement';
import UserProfile from './UserProfile';
import MessageCenter from './MessageCenter';
import { sensorService } from '../../services/sensorService';
import { useAuth } from '../../contexts/AuthContext';
import { getNdviColor } from '../../utils/ndviColors';
import { primaryValue } from '../../utils/sensorReading';
import {
  LayoutDashboard, Map, BarChart2, ShieldAlert, Users,
  KeyRound, Database, ClipboardList, MessageSquare, Menu,
  ChevronRight, Trash2, Sun, Leaf, Droplets, Search, X,
  SatelliteDish,
} from 'lucide-react';

import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import NotificationBell from '../Common/NotificationBell';
import { useNotifications } from '../../contexts/NotificationContext';
import AiAdvisor from './AiAdvisor';
import ReportGenerator from '../Reports/ReportGenerator';
import ApiKeyManager from './ApiKeyManager';
import BackupManager from './BackupManager';
import AuditLog from './AuditLog';
import IrrigationControl from '../Map/IrrigationControl';
import MapTimeline from '../Map/MapTimeline';
import DraggableFab from '../Map/DraggableFab';
import DssCenter from './DssCenter';
import SensorDevicePanel from './SensorDevicePanel';
import WaterBalanceBox from './WaterBalanceBox';
import { stationEt0 } from '../../utils/fieldclimateGroups';
import { et0Display } from '../../utils/waterBalanceView';
import { waterBalanceService } from '../../services/waterBalanceService';
import { sensorEntries } from '../Map/weatherStationCard';

const Dashboard = () => {
  const { t, language } = useLanguage();
  const currentLocale = language === 'en' ? 'en-US' : 'tr-TR';
  const { user, profile, setProfile, logout } = useAuth();
  const { addNotification } = useNotifications();
  const isAdmin = profile?.role?.toUpperCase() === 'ADMIN';

  /* --- STATE --- */
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  // Sentinel & Heatmap States
  const [sentinelHistory, setSentinelHistory] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [foundImages, setFoundImages] = useState([]); // Store found images in state
  const [activeIndex, setActiveIndex] = useState('NDVI'); // 'NDVI' or 'NDRE'
  const [sensors, setSensors] = useState([]); // Added sensors state
  const [selectedSensor, setSelectedSensor] = useState(null); // For sensor details
  const [isAddingSensor, setIsAddingSensor] = useState(false);
  const [pendingSensorId, setPendingSensorId] = useState(null);
  const [isSensorModalOpen, setIsSensorModalOpen] = useState(false);
  const [availableSensorIds, setAvailableSensorIds] = useState([]);
  const [selectedSensorIds, setSelectedSensorIds] = useState([]); // Batch selection
  const [batchSensorMode, setBatchSensorMode] = useState(false); // Toggle between single/batch mode
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [irrigationOn, setIrrigationOn] = useState(false);
  const [showIrrPanel, setShowIrrPanel] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [expandedFieldId, setExpandedFieldId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleIrrigationChange = (isOn) => setIrrigationOn(isOn);
  const [profileSubTab, setProfileSubTab] = useState('personal');
  const [kcInputs, setKcInputs] = useState({});   // fieldId → kc string input
  const [kcSaving, setKcSaving] = useState({});   // fieldId → boolean

  // Water balance per field from the server (ET₀, deficit, recommendation) — fieldId → balance.
  // Weather data changes hourly, so a 5-min refresh is plenty (sensors refresh every 5 s).
  const [balances, setBalances] = useState({});
  const loadBalances = useCallback(async () => {
    try {
      const list = await waterBalanceService.fetchAll();
      setBalances(Object.fromEntries(list.map(b => [b.fieldId, b])));
    } catch (error) {
      console.error('Error loading water balance:', error);
    }
  }, []);
  useEffect(() => {
    loadBalances();
    const id = setInterval(loadBalances, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [loadBalances]);

  // ET₀ card of the selected field: server rolling-24 h FAO-56, same value irrigation uses.
  const et0Result = useMemo(() => {
    if (!selectedField) return null;
    const b = balances[selectedField.id];
    const d = et0Display(b, language);
    if (d.value == null) return null;
    const kc = b.kc ?? selectedField.kc ?? null;
    const etc = kc != null ? Math.round(d.value * kc * 100) / 100 : null;
    return { et0: d.value, etc, kc, title: d.title, stale: d.stale };
  }, [selectedField, balances, language]);
  const [showAiAdvisor, setShowAiAdvisor] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    loadFields();
    loadSensors(); // Fetch sensors on mount

    const handleTriggerAdd = () => handleAddSensor();
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('trigger-add-sensor', handleTriggerAdd);
    document.addEventListener('mousedown', handleClickOutside);

    // Mesaj sayacı başlangıç
    checkUnreadMessages();
    const messageInterval = setInterval(checkUnreadMessages, 10000);

    // Realtime sensor polling — keeps map markers + readings fresh
    const sensorInterval = setInterval(loadSensors, 5000);

    return () => {
      document.removeEventListener('trigger-add-sensor', handleTriggerAdd);
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(messageInterval);
      clearInterval(sensorInterval);
    };
  }, []);

  const prevUnreadRef = React.useRef(0);
  const checkUnreadMessages = async () => {
    try {
      const data = await messageService.getUnreadSummary();
      const count = data.unreadCount || 0;
      if (count > prevUnreadRef.current) {
        addNotification({
          type: 'message',
          text: language === 'en'
            ? `You have ${count} unread message${count > 1 ? 's' : ''}`
            : `${count} okunmamış mesajınız var`,
        });
      }
      prevUnreadRef.current = count;
      setUnreadCount(count);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error checking unread messages:', error);
      }
    }
  };

  // Tarla değiştiğinde analiz verilerini temizle ve varsa olanı yükle
  useEffect(() => {
    if (selectedField) {
      // Önceki tarladan kalan verileri temizle
      // setSentinelHistory(null); // DON'T CLEAR HERE or chart flickers. Let loadExistingAnalysis replace it.
      // setHeatmapData(null); // DON'T CLEAR - Let loadExistingAnalysis set the correct data for this field
      setFoundImages([]);

      // Varsa bu tarlanın analiz geçmişini yükle
      loadExistingAnalysis(selectedField.id);
    }
  }, [selectedField]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const data = await fieldService.getFields();
      setFields(data);
      if (data.length > 0) {
        setSelectedField(data[0]);
      }
    } catch (error) {
      console.error('Error loading fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSensors = async () => {
    try {
      const data = await sensorService.getAllSensorsLatest();
      setSensors(data.filter(s => s.insighioDeviceId || s.fieldclimateStationId || s.apiKeyName));
    } catch (error) {
      console.error('Error loading sensors:', error);
    }
  };

  const loadExistingAnalysis = async (fieldId) => {
    try {
      const history = await fieldService.getNdviTimeline(fieldId);
      if (history && history.length > 0) {
        const formattedHistory = history.map(item => ({
          ...item,
          date: item.acquisitionDate,
          ndviImagePath: getImageUrl(item.ndviImagePath),
          ndreImagePath: getImageUrl(item.ndreImagePath),
          ndmiImagePath: getImageUrl(item.ndmiImagePath),
          heatmapColor: getNdviColor(item.meanNdvi),

          // Legacy support or fallback if needed
          meanNdvi: item.meanNdvi,
          meanNdre: item.meanNdre
        }));
        setSentinelHistory(formattedHistory);

        // Son görüntüyü haritaya yansıt
        const lastPoint = formattedHistory[formattedHistory.length - 1];
        if (lastPoint) {
          setHeatmapData({
            color: getNdviColor(lastPoint.meanNdvi), // Use helper directly to avoid prop mismatch
            date: lastPoint.date,

            ndvi: lastPoint.meanNdvi,
            ndviImagePath: lastPoint.ndviImagePath,
            minNdvi: lastPoint.minNdvi,
            maxNdvi: lastPoint.maxNdvi,
            stdNdvi: lastPoint.stdNdvi,

            ndre: lastPoint.meanNdre,
            ndreImagePath: lastPoint.ndreImagePath,
            minNdre: lastPoint.minNdre,
            maxNdre: lastPoint.maxNdre,
            stdNdre: lastPoint.stdNdre,

            ndmi: lastPoint.meanNdmi,
            ndmiImagePath: lastPoint.ndmiImagePath,
            minNdmi: lastPoint.minNdmi,
            maxNdmi: lastPoint.maxNdmi,
            stdNdmi: lastPoint.stdNdmi,

            zonalStats: lastPoint.zonalStats
          });
        }
      }
      // DON'T clear state if no history - field might just not have analysis yet
      // The previous field's data will remain visible until this field gets analyzed
    } catch (error) {
      console.error('Error loading existing analysis:', error);
      // DON'T clear on error either - might be network issue
    }
  };

  /* --- ACTION HANDLERS --- */

  /* --- ACTION HANDLERS --- */

  const handleCreateField = async (fieldData) => {
    try {
      setLoading(true);
      const newField = await fieldService.createField(fieldData);
      setFields(prev => [...prev, newField]);
      setSelectedField(newField);
      alert('Tarla başarıyla oluşturuldu!');
    } catch (error) {
      console.error('Error creating field:', error);
      alert('Tarla oluşturulurken hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchImages = async () => {
    if (!selectedField) {
      alert('Lütfen önce bir tarla seçin!');
      return;
    }

    try {
      setLoading(true);

      // 1. DB'deki en son analiz tarihini bul — GEE'ye sadece o tarihten sonrasını sor
      const existingHistory = await fieldService.getNdviTimeline(selectedField.id);
      let geeStartDate = null;
      if (existingHistory && existingHistory.length > 0) {
        // Son analiz tarihinden 1 gün öncesini başlangıç yap (o günü de kapsasın)
        const lastDate = new Date(existingHistory[existingHistory.length - 1].acquisitionDate);
        lastDate.setDate(lastDate.getDate() - 1);
        geeStartDate = lastDate.toISOString();
      }

      // 2. GEE'den görüntüleri çek — varsa son tarihten itibaren, yoksa son 1 yıl
      const images = await imageService.searchSentinelImages(selectedField.id, geeStartDate);

      if (images.length === 0) {
        alert(t('alerts.image_search.no_images'));
        return;
      }

      // GEE oldest-first: images[0]=oldest, images[last]=newest
      const newestDate = new Date(images[images.length - 1].date).toLocaleDateString(currentLocale);
      const oldestDate = new Date(images[0].date).toLocaleDateString(currentLocale);

      // 3. DB'de zaten analiz edilmiş itemId'leri çek
      const analyzedIds = await fieldService.getAnalyzedItemIds(selectedField.id);
      const analyzedSet = new Set(analyzedIds);

      // 3. Sadece DB'de olmayan görüntüleri bul
      const missingImages = images.filter(img => !analyzedSet.has(img.id));

      const confirmMsg = `${t('alerts.image_search.scanning')}\n${t('alerts.image_search.location')}: ${selectedField.name}\n\n✅ ${t('alerts.image_search.found').replace('{{count}}', images.length)}\n${t('alerts.image_search.newest')}: ${newestDate}\n${t('alerts.image_search.oldest')}: ${oldestDate}\n\n📦 Veritabanında: ${analyzedIds.length} görüntü\n🆕 Eksik olan: ${missingImages.length} görüntü\n\n${missingImages.length > 0 ? 'Eksik görüntüler analiz edilecek. Devam etmek istiyor musunuz?' : 'Tüm görüntüler zaten analiz edilmiş. Yine de yeniden yüklemek ister misiniz?'}`;

      if (!window.confirm(confirmMsg)) return;

      // 4. Eksik görüntüleri analiz et (hepsi zaten varsa bile DB'den yükle)
      if (missingImages.length > 0) {
        await imageService.analyzeImages(missingImages.slice(0, 52), selectedField.id, false);
      }

      // 5. Analiz bitti — timeline'ı HER ZAMAN DB'den yükle (setSentinelHistory(null) YOK)
      await loadExistingAnalysis(selectedField.id);

      setActiveTab('map');
      alert(`✅ Tamamlandı! ${missingImages.length > 0 ? `${missingImages.length} yeni görüntü analiz edildi.` : 'Timeline güncellendi.'}`);

    } catch (error) {
      console.error('Search error:', error);
      const msg = error.response?.data?.message || error.response?.data?.error || error.message;
      alert(`${t('alerts.image_search.search_error')}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeNdvi = async (preLoadedImages = null, force = false) => {
    if (!selectedField) {
      alert('Lütfen önce bir tarla seçin!');
      return;
    }

    try {
      setLoading(true);

      // GEE'den görüntüleri çek
      const images = await imageService.searchSentinelImages(selectedField.id);
      if (!images || images.length === 0) {
        alert(t('alerts.image_search.no_images'));
        return;
      }

      // DB'de olmayan veya force=true ise hepsini analiz et
      let imagesToProcess = images;
      if (!force) {
        const analyzedIds = await fieldService.getAnalyzedItemIds(selectedField.id);
        const analyzedSet = new Set(analyzedIds);
        imagesToProcess = images.filter(img => !analyzedSet.has(img.id));
      }

      if (imagesToProcess.length > 0) {
        await imageService.analyzeImages(imagesToProcess.slice(0, 52), selectedField.id, force);
      }

      // Timeline'ı her zaman DB'den yükle
      await loadExistingAnalysis(selectedField.id);

      const history = await fieldService.getNdviTimeline(selectedField.id);
      const lastPoint = history?.[history.length - 1];

      if (lastPoint) {
        const alertMsg = t('alerts.image_search.analysis_details')
          .replace('{{count}}', history.length)
          .replace('{{date}}', new Date(lastPoint.acquisitionDate).toLocaleDateString(currentLocale))
          .replace('{{ndvi}}', lastPoint.meanNdvi?.toFixed(3))
          .replace('{{ndre}}', lastPoint.meanNdre?.toFixed(3));
        alert(`${t('alerts.image_search.analysis_success')}\n\n${alertMsg}`);
        setActiveTab('map');
      } else {
        alert(t('alerts.image_search.no_meaningful_data'));
      }

    } catch (error) {
      console.error('Error analyzing NDVI:', error);
      const msg = error.response?.data?.details || error.message;
      alert(`${t('alerts.analysis_error')} ${msg}\n\n${t('alerts.image_search.contact_admin')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTimelineClick = (dataPoint) => {
    if (!dataPoint) return;

    // Timeline'dan gelen veriyi eşleştir
    // dataPoint.date contains the date string from the timeline
    const clickedDate = new Date(dataPoint.date).toISOString().split('T')[0];

    const historyItem = sentinelHistory?.find(h => {
      const historyDate = new Date(h.date).toISOString().split('T')[0];
      return historyDate === clickedDate;
    });

    if (historyItem) {
      setHeatmapData({
        color: historyItem.heatmapColor || getNdviColor(historyItem.meanNdvi),
        date: historyItem.date,

        ndvi: historyItem.meanNdvi,
        ndviImagePath: historyItem.ndviImagePath,
        minNdvi: historyItem.minNdvi,
        maxNdvi: historyItem.maxNdvi,
        stdNdvi: historyItem.stdNdvi,

        ndre: historyItem.meanNdre,
        ndreImagePath: historyItem.ndreImagePath,
        minNdre: historyItem.minNdre,
        maxNdre: historyItem.maxNdre,
        stdNdre: historyItem.stdNdre,

        ndmi: historyItem.meanNdmi,
        ndmiImagePath: historyItem.ndmiImagePath,
        minNdmi: historyItem.minNdmi,
        maxNdmi: historyItem.maxNdmi,
        stdNdmi: historyItem.stdNdmi,

        zonalStats: historyItem.zonalStats
      });

      if (activeTab !== 'map') {
        setActiveTab('map');
      }

      // Kullanıcı deneyimi: Alert yerine sessiz geçiş veya toast daha iyi olur ama şimdilik alert
      // alert yerine konsola yazalım, kullanıcı haritaya geçince görür
      console.log('Harita güncellendi:', historyItem);
    }
  };

  // Güncelleme İşlemi
  const handleUpdateField = async (id, updates) => {
    try {
      setLoading(true);
      const updatedField = await fieldService.updateField(id, updates);

      setFields(prev => prev.map(f => String(f.id) === String(id) ? updatedField : f));

      if (selectedField && String(selectedField.id) === String(id)) {
        setSelectedField(updatedField);
      }
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Güncelleme hatası: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Silme işlemi
  const handleDeleteField = async (fieldId) => {
    // Harita üzerinden silinirken onay penceresi zaten çıkmış olabilir veya buradan kontrol edilebilir
    // Çift onay olmaması için context'e göre bakılabilir ama şimdilik güvenli olsun
    // Not: Leaflet draw delete event'i tetiklenince harita UI'ı zaten siler, biz state'i senkronize etmeliyiz.

    try {
      setLoading(true);
      await fieldService.deleteField(fieldId);
      const updatedFields = fields.filter(f => String(f.id) !== String(fieldId));
      setFields(updatedFields);

      if (selectedField && String(selectedField.id) === String(fieldId)) {
        setSelectedField(null);
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      alert('Silme hatası: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Çıkış hatası:', err);
    }
  };

  const handleAddSensor = async () => {
    try {
      setLoading(true);
      const sensorData = await sensorService.getAllSensorsLatest();
      // Yalnızca ID'leri alalım
      const ids = sensorData.map(s => s.sensorId);
      setAvailableSensorIds(ids);
      setIsSensorModalOpen(true);
    } catch (error) {
      console.error('Sensörler yüklenirken hata:', error);
      alert('Sensör listesi alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  const handleSensorMove = async (sensorId, lat, lng, newFieldId, oldFieldId) => {
    // Eğer tarla değişmişse onay al
    if (newFieldId && oldFieldId && String(newFieldId) !== String(oldFieldId)) {
      const newFieldName = fields.find(f => String(f.id) === String(newFieldId))?.name || 'Yeni Tarla';
      const confirmMove = window.confirm(`Bu sensör zaten başka bir tarlaya bağlı. Sensörü "${newFieldName}" tarlası ile ilişkilendirmek istediğinize emin misiniz?`);

      if (!confirmMove) {
        // İptal edilirse sensörleri tekrar yükleyerek eski yerlerine döndür
        loadSensors();
        return;
      }
    }

    try {
      setLoading(true);
      await sensorService.updateSensorLocation(sensorId, { lat, lng, fieldId: newFieldId });

      // State'i güncelle
      setSensors(prev => prev.map(s =>
        s.sensorId === sensorId
          ? { ...s, lat, lng, fieldId: newFieldId }
          : s
      ));

      console.log(`Sensör ${sensorId} yeni konuma taşındı.`);
    } catch (error) {
      console.error('Sensör taşıma hatası:', error);
      alert('Konum güncellenemedi.');
      loadSensors(); // Hata durumunda eski veriyi geri çek
    } finally {
      setLoading(false);
    }
  };

  const selectSensorForPlacement = (id) => {
    setIsSensorModalOpen(false);
    setIsAddingSensor(true);
    setPendingSensorId(id);
    alert('🗺️ Harita üzerinde sensörün konumunu seçmek için bir noktaya tıklayın.');
  };

  // Toggle sensor selection in batch mode
  const toggleSensorSelection = (sensorId) => {
    setSelectedSensorIds(prev =>
      prev.includes(sensorId)
        ? prev.filter(id => id !== sensorId)
        : [...prev, sensorId]
    );
  };

  // Calculate field center from geometry
  const getFieldCenter = (field) => {
    if (!field?.geometry?.coordinates?.[0]) return null;
    const coords = field.geometry.coordinates[0];
    const lats = coords.map(c => c[1]);
    const lngs = coords.map(c => c[0]);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2
    };
  };

  // Handle batch sensor addition to selected field
  const handleBatchSensorAdd = async () => {
    if (selectedSensorIds.length === 0) {
      alert('⚠️ Lütfen en az bir sensör seçin.');
      return;
    }
    if (!selectedField) {
      alert('⚠️ Lütfen önce bir tarla seçin.');
      return;
    }

    const center = getFieldCenter(selectedField);
    if (!center) {
      alert('⚠️ Tarla koordinatları bulunamadı.');
      return;
    }

    try {
      setLoading(true);

      // Distribute sensors in a circle around the field center
      const sensorUpdates = selectedSensorIds.map((sensorId, index) => {
        const angle = (2 * Math.PI * index) / selectedSensorIds.length;
        const radius = 0.0003; // ~30 meters offset
        return {
          sensorId,
          lat: center.lat + radius * Math.sin(angle),
          lng: center.lng + radius * Math.cos(angle),
          fieldId: selectedField.id
        };
      });

      await sensorService.batchUpdateSensorLocations(sensorUpdates);

      // Update local state
      setSensors(prev => {
        const updated = [...prev];
        sensorUpdates.forEach(su => {
          const idx = updated.findIndex(s => s.sensorId === su.sensorId);
          if (idx >= 0) {
            updated[idx] = { ...updated[idx], lat: su.lat, lng: su.lng, fieldId: su.fieldId };
          }
        });
        return updated;
      });

      setIsSensorModalOpen(false);
      setSelectedSensorIds([]);
      setBatchSensorMode(false);
      alert(`✅ ${sensorUpdates.length} sensör "${selectedField.name}" tarlasına başarıyla eklendi!`);
    } catch (error) {
      console.error('Toplu sensör ekleme hatası:', error);
      alert('Sensörler eklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-logo-container">
            <button
              className="sidebar-toggle-btn"
              onClick={() => setSidebarOpen(v => !v)}
              title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <Menu size={16} />
            </button>
            <div className="brand-text">
              <span className="brand-main">SWAMED</span>
            </div>
          </div>
          <div className="header-actions">
            {isAdmin && (
              <button
                className="btn-primary"
                onClick={() => {
                  setSelectedField(null);
                  setActiveTab('map');
                  setTimeout(() => {
                    alert(`${t('dashboard.mapMode.title')}\n\n${t('dashboard.mapMode.instructions')}`);
                  }, 100);
                }}
              >
                + {t('header.new_field')}
              </button>
            )}

            <NotificationBell language={language} />
            <LanguageSwitcher />

            {/* Profile Dropdown */}
            <div className="profile-dropdown-container" ref={dropdownRef}>
              <button
                className="profile-trigger-btn"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                <div className="profile-trigger-avatar-box">
                  {(profile?.firstName || profile?.name || user?.email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="profile-trigger-text">
                  <span className="profile-trigger-name">
                    {profile?.firstName ? `${profile.firstName} ${profile.lastName}` : (profile?.name || user?.email || 'Kullanıcı')}
                  </span>
                </div>
                <ChevronRight size={12} className={`dropdown-arrow ${showProfileDropdown ? 'open' : ''}`} style={{ transform: showProfileDropdown ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }} />
              </button>

              {showProfileDropdown && (
                <div className="header-dropdown-menu">
                  <div className="dropdown-profile-header">
                    <div className="header-avatar-box">
                      {(profile?.firstName || profile?.name || user?.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="header-user-info">
                      <span className="header-user-name">
                        {profile?.firstName ? `${profile.firstName} ${profile.lastName}` : (profile?.name || user?.email || 'Kullanıcı')}
                      </span>
                      <span className="header-user-email">{profile?.email}</span>
                      <span className={`role-tag ${profile?.role?.toLowerCase()}`}>
                        {profile?.role === 'ADMIN' ? t('header.admin') : t('header.user')}
                      </span>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button onClick={() => { setActiveTab('profile'); setProfileSubTab('personal'); setShowProfileDropdown(false); }}>
                    <span className="icon"><Users size={15} /></span> {t('header.profile_info')}
                  </button>
                  <button onClick={() => { setActiveTab('profile'); setProfileSubTab('security'); setShowProfileDropdown(false); }}>
                    <span className="icon"><KeyRound size={15} /></span> {t('header.change_password')}
                  </button>
                  <div className="dropdown-divider"></div>
                  <button className="logout-item" onClick={handleLogout}>
                    <span className="icon"><X size={15} /></span> {t('header.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header >

      <div className="dashboard-content">
        {/* Sol Sidebar - Tarla Listesi */}
        <aside className={`sidebar${sidebarOpen ? '' : ' sidebar-collapsed'}${expandedFieldId ? ' sidebar-field-open' : ''}`}>
          <div className="sidebar-header">
            <h2>{t('dashboard.my_fields')}</h2>
            <span className="field-count">{fields.length} {t('dashboard.field_count')}</span>
          </div>

          <div className="search-container">
            <input
              type="text"
              className="field-search-input"
              placeholder={t('dashboard.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}><X size={12} /></button>
            )}
          </div>

          <div className="fields-list">
            {loading && fields.length === 0 ? (
              <div className="loading-message">{t('common.loading')}</div>
            ) : (() => {
              const filteredFields = fields.filter(field => {
                const query = searchQuery.toLowerCase();
                return (
                  field.name?.toLowerCase().includes(query) ||
                  field.cropType?.toLowerCase().includes(query) ||
                  field.user?.name?.toLowerCase().includes(query)
                );
              });

              if (filteredFields.length === 0) {
                return (
                  <div className="empty-message">
                    <p>{searchQuery ? t('dashboard.no_match') : t('dashboard.no_fields')}</p>
                    {!searchQuery && <p>{t('dashboard.start_hint')}</p>}
                  </div>
                );
              }

              return filteredFields.map((field) => {
                const isSelected = selectedField?.id === field.id;
                const isExpanded = expandedFieldId === field.id;
                return (
                  <div
                    key={field.id}
                    className={`field-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                  >
                    {/* Accordion trigger row */}
                    <div
                      className="field-card-main"
                      onClick={() => {
                        setSelectedField(field);
                        setExpandedFieldId(isExpanded ? null : field.id);
                      }}
                    >
                      <div className="field-card-header">
                        <h4>{field.name}</h4>
                        <div className="field-card-header-right">
                          <span className="field-area">{field.area} ha</span>
                          <ChevronRight size={14} className={`field-accordion-arrow ${isExpanded ? 'open' : ''}`} />
                        </div>
                      </div>
                    </div>

                    {/* Accordion body */}
                    {isExpanded && (
                      <div className="field-card-body">
                        <div className="field-info-row">
                          <span className="info-label">{t('field.crop')}:</span>
                          <span className="info-value">{field.cropType || '-'}</span>
                        </div>
                        {field.user && (
                          <div className="field-info-row">
                            <span className="info-label">{t('field.owner')}:</span>
                            <span className="info-value">{field.user.name}</span>
                          </div>
                        )}
                        {field.plantingDate && (
                          <div className="field-info-row">
                            <span className="info-label">{t('field.planting')}:</span>
                            <span className="info-value">
                              {new Date(field.plantingDate).toLocaleDateString(currentLocale)}
                            </span>
                          </div>
                        )}

                        {/* ── ET₀ / Kc / ETc panel ── */}
                        {(() => {
                          // Server rolling-24 h FAO-56 for every field — the value irrigation uses.
                          const et0d = et0Display(balances[field.id], language);
                          const et0val = et0d.value;

                          const kcStr  = kcInputs[field.id] ?? (field.kc != null ? String(field.kc) : '');
                          const kcNum  = parseFloat(kcStr);
                          const etcVal = (et0val != null && !isNaN(kcNum) && kcNum > 0)
                            ? Math.round(et0val * kcNum * 100) / 100
                            : null;

                          const handleSaveKc = async () => {
                            if (isNaN(kcNum) || kcNum <= 0) return;
                            setKcSaving(p => ({ ...p, [field.id]: true }));
                            try {
                              await handleUpdateField(field.id, { kc: kcNum });
                            } finally {
                              setKcSaving(p => ({ ...p, [field.id]: false }));
                            }
                          };

                          return (
                            <div className="etc-panel">
                              {/* ET₀ row */}
                              <div className="etc-row">
                                <span className="etc-label etc-label--et0">
                                  <Sun size={11} /> ET₀ Reference
                                </span>
                                <span className="etc-val etc-val--et0 data-val">
                                  {(() => {
                                    const en = language === 'en';
                                    const loc = en ? 'en-GB' : 'tr-TR';
                                    const fc = stationEt0(sensors, field.id);
                                    if (et0val == null && !fc) return <span className="etc-empty">—</span>;
                                    const fcTitle = fc && `FieldClimate ET₀ (${en ? 'daily' : 'günlük'})${fc.at ? ` · ${new Date(fc.at).toLocaleDateString(loc)}` : ''}`;
                                    return <>
                                      <span title={et0d.title} style={et0d.title ? { cursor: 'help', ...(et0d.stale ? { opacity: 0.55 } : {}) } : undefined}>{et0val ?? '—'}</span>
                                      {fc && <span title={fcTitle} style={{ cursor: 'help' }}>/{fc.value}</span>}
                                      {' '}<small>mm/day</small>
                                    </>;
                                  })()}
                                </span>
                              </div>

                              {/* Live hourly demand (indicator only — irrigation uses the 24 h value) */}
                              {et0d.perHour != null && (
                                <div className="etc-row">
                                  <span className="etc-label etc-label--et0" title={language === 'en' ? 'Hourly FAO-56 ET₀ of the latest hour — live demand indicator, not used for irrigation amounts' : 'Son saatin saatlik FAO-56 ET₀ değeri — anlık talep göstergesi, sulama miktarında kullanılmaz'} style={{ cursor: 'help' }}>
                                    <Sun size={11} /> {language === 'en' ? 'ET₀ now' : 'ET₀ anlık'}
                                  </span>
                                  <span className="etc-val etc-val--et0 data-val" style={et0d.stale ? { opacity: 0.55 } : undefined}>
                                    {et0d.perHour} <small>mm/h</small>
                                  </span>
                                </div>
                              )}

                              {/* Kc row */}
                              <div className="etc-row">
                                <span className="etc-label etc-label--kc">
                                  <Leaf size={11} /> Kc Crop Coeff.
                                </span>
                                {isAdmin ? (
                                  <div className="etc-kc-edit">
                                    <input
                                      type="number"
                                      min="0.1" max="2" step="0.01"
                                      value={kcStr}
                                      placeholder="0.00"
                                      onChange={e => setKcInputs(p => ({ ...p, [field.id]: e.target.value }))}
                                      className="etc-kc-input data-val"
                                    />
                                    <button
                                      onClick={handleSaveKc}
                                      disabled={kcSaving[field.id] || isNaN(kcNum) || kcNum <= 0}
                                      className="etc-kc-save"
                                    >
                                      {kcSaving[field.id] ? '…' : 'Kaydet'}
                                    </button>
                                  </div>
                                ) : (
                                  <span className="etc-val etc-val--kc data-val">
                                    {field.kc != null ? field.kc : '—'}
                                  </span>
                                )}
                              </div>

                              {/* ETc result row */}
                              <div className="etc-row etc-row--result">
                                <span className="etc-label etc-label--etc">
                                  <Droplets size={11} /> ET₀×Kc
                                </span>
                                <span className="etc-etc-badge data-val">
                                  {etcVal != null
                                    ? <>{etcVal} <small>mm/day</small></>
                                    : '—'}
                                </span>
                              </div>
                            </div>
                          );
                        })()}

                        <WaterBalanceBox
                          balance={balances[field.id]}
                          field={field}
                          sensors={sensors}
                          isAdmin={isAdmin}
                          language={language}
                          onChanged={loadBalances}
                        />

                        {/* Sensors linked to this field — grouped by category */}
                        {(() => {
                          const fieldSensors = sensors.filter(s => s.fieldId === field.id);
                          if (fieldSensors.length === 0) return null;
                          return (
                            <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                              <SensorDevicePanel
                                sensors={fieldSensors}
                                language={language}
                                selectedSensorId={selectedSensor?.id ?? null}
                                onSensorClick={(sensor) => {
                                  setSelectedSensor({ ...sensor, fieldName: field.name });
                                }}
                                noWrap
                              />
                            </div>
                          );
                        })()}

                        {/* Admin: add sensor + delete buttons inside expanded body */}
                        {isAdmin && (
                          <div className="field-admin-row">
                            <button
                              style={{ background: '#e8f5e9', border: '1px dashed #a5d6a7', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: '#2e7d32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                              onClick={(e) => { e.stopPropagation(); setSelectedField(field); setActiveTab('apikeys'); }}
                              title="Add sensor to this field"
                            >
                              <SatelliteDish size={11} /> {language === 'en' ? 'Add Sensor' : 'Sensör Ekle'}
                            </button>
                            <button
                              className="field-delete-btn"
                              onClick={(e) => { e.stopPropagation(); handleDeleteField(field.id); }}
                              title="Delete Field"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </aside>

        {/* Ana İçerik */}
        <main className="main-content">
          <div className="tab-menu">
            <button
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <LayoutDashboard size={14} /> {t('sidebar.overview')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'map' ? 'active' : ''}`}
              onClick={() => setActiveTab('map')}
              disabled={!selectedField}
            >
              <Map size={14} /> {t('sidebar.map')}
            </button>
            <button
              className={`tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('analysis')}
              disabled={!selectedField}
            >
              <BarChart2 size={14} /> {t('sidebar.analysis')}
            </button>
            {isAdmin && (
              <button
                className={`tab-btn ${activeTab === 'risk' ? 'active' : ''}`}
                onClick={() => setActiveTab('risk')}
              >
                <ShieldAlert size={14} /> {language === 'en' ? 'DSS' : 'Karar Destek'}
              </button>
            )}
            {isAdmin && (
              <button
                className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <Users size={14} /> {language === 'en' ? 'Users' : 'Kullanıcılar'}
              </button>
            )}
            {isAdmin && (
              <button
                className={`tab-btn ${activeTab === 'apikeys' ? 'active' : ''}`}
                onClick={() => setActiveTab('apikeys')}
              >
                <KeyRound size={14} /> API
              </button>
            )}
            {isAdmin && (
              <button
                className={`tab-btn ${activeTab === 'backup' ? 'active' : ''}`}
                onClick={() => setActiveTab('backup')}
              >
                <Database size={14} /> Backup
              </button>
            )}
            {isAdmin && (
              <button
                className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
                onClick={() => setActiveTab('audit')}
              >
                <ClipboardList size={14} /> Audit
              </button>
            )}
            <button
              className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              <MessageSquare size={14} /> Support
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </button>
          </div>


          <div className="tab-content">
            {activeTab === 'overview' && (
              <div className="overview-panel">
                <h2>{t('dashboard.welcome')}</h2>
                <div className="welcome-content">
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon"><Leaf size={20} /></div>
                      <div className="stat-info">
                        <span className="stat-value">{fields.length}</span>
                        <span className="stat-label">{t('dashboard.total_fields')}</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon"><Map size={20} /></div>
                      <div className="stat-info">
                        <span className="stat-value">
                          {fields.reduce((sum, f) => sum + f.area, 0).toFixed(1)}
                        </span>
                        <span className="stat-label">{t('dashboard.total_area')}</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon"><SatelliteDish size={20} /></div>
                      <div className="stat-info">
                        <span className="stat-value">Sentinel-2</span>
                        <span className="stat-label">{t('dashboard.satellite_type')}</span>
                      </div>
                    </div>
                  </div>



                  {/* Sensor list — visible on mobile where sidebar is compact */}
                  {(() => {
                    const visibleSensors = selectedField
                      ? sensors.filter(s => s.fieldId === selectedField.id)
                      : sensors;
                    if (visibleSensors.length === 0) return null;
                    return (
                    <div className="mobile-sensor-list">
                      <h3 className="mobile-sensor-title">
                        📡 {language === 'en' ? 'Sensors' : 'Sensörler'}
                        {selectedField && <span style={{fontWeight:400, fontSize:'0.72rem', marginLeft:6}}>— {selectedField.name}</span>}
                      </h3>
                      <div className="mobile-sensor-grid">
                        {visibleSensors.flatMap(s => sensorEntries(s, language === 'en' ? 'en' : 'tr')).map(({ key, label, sensor }) => {
                          const val = sensor.fcGroup ? null : primaryValue(sensor);
                          const sensorEmoji = { Temperature:'🌡️', Humidity:'💧', Moisture:'🌱', SoilTemperature:'🌡️', Rainfall:'🌧️', WindSpeed:'💨', Multi:'📊' };
                          const unit = sensor.type === 'Temperature' || sensor.type === 'SoilTemperature' ? '°C' :
                            sensor.type === 'Rainfall' ? 'mm' : sensor.type === 'WindSpeed' ? 'm/s' : '%';
                          return (
                            <button key={key} className="mobile-sensor-card"
                              onClick={() => setSelectedSensor({ ...sensor, fieldName: fields.find(f => f.id === sensor.fieldId)?.name || '' })}>
                              <span className="msc-icon">{sensor.fcGroup ? (sensor.fcGroup === 'weather' ? '🌤️' : '💨') : (sensorEmoji[sensor.type] || '📡')}</span>
                              <span className="msc-name">{label}</span>
                              <span className="msc-val">{sensor.fcGroup ? '' : (val != null ? `${Number(val).toFixed(1)} ${unit}` : '—')}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}

                  {/* Hızlı İşlemler - only for ADMIN */}
                  {selectedField && isAdmin && (
                    <div className="quick-actions">
                      <h3>{t('dashboard.quick_actions')} - {selectedField.name}</h3>
                      <div className="action-buttons">
                        <button
                          className="action-btn"
                          onClick={handleSearchImages}
                          disabled={loading}
                        >
                          🔍 {t('dashboard.search_images')}
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleAnalyzeNdvi()}
                          disabled={loading || (!foundImages || foundImages.length === 0)}
                          style={{
                            opacity: (!foundImages || foundImages.length === 0) ? 0.5 : 1,
                            cursor: (!foundImages || foundImages.length === 0) ? 'not-allowed' : 'pointer'
                          }}
                          title={(!foundImages || foundImages.length === 0) ? "Click 'Search Images' first" : "Start Analysis"}
                        >
                          🌱 {t('dashboard.ndvi_analysis')}
                        </button>
                        {sentinelHistory && sentinelHistory.length > 0 && (
                          <button
                            className="action-btn"
                            onClick={() => {
                              if (window.confirm('Bu tarlanın analiz verileri silinip tekrar hesaplanacak (Kar/Buz dahil). Onaylıyor musunuz?')) {
                                handleAnalyzeNdvi(null, true);
                              }
                            }}
                            disabled={loading}
                            style={{
                              background: '#f59e0b',
                              marginTop: '8px',
                              borderTop: '1px solid rgba(255,255,255,0.2)'
                            }}
                            title="Re-fetch and recalculate data from server"
                          >
                            🔄 {t('dashboard.refresh_analysis')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'map' && (
              <div className="map-panel">

                {/* Harita — timeline varsa alt boşluk bırakır */}
                <div className="map-leaflet-area">
                  <FieldMap
                    fields={fields}
                    selectedField={selectedField}
                    onFieldSelect={setSelectedField}
                    onFieldCreate={handleCreateField}
                    onFieldUpdate={handleUpdateField}
                    onFieldDelete={handleDeleteField}
                    overlayData={heatmapData}
                    timelineData={null}
                    onTimelineSelect={handleTimelineClick}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    sensors={sensors}
                    selectedSensor={selectedSensor}
                    selectedSensorId={selectedSensor?.id ?? null}
                    onSensorClick={(sensor) => {
                      const f = fields.find(f => f.id === sensor.fieldId);
                      setSelectedSensor({ ...sensor, fieldName: f?.name || null });
                    }}
                    isAddingSensor={isAddingSensor}
                    onSensorMove={handleSensorMove}
                    readOnly={!isAdmin}
                    isAdmin={isAdmin}
                    irrigationOn={irrigationOn}
                    onSensorAdd={async (lat, lng, fieldId) => {
                      if (pendingSensorId) {
                        const existingSensor = sensors.find(s => s.sensorId === pendingSensorId);
                        if (existingSensor?.fieldId && fieldId && String(existingSensor.fieldId) !== String(fieldId)) {
                          const oldFieldName = fields.find(f => String(f.id) === String(existingSensor.fieldId))?.name || 'Mevcut Tarla';
                          const newFieldName = fields.find(f => String(f.id) === String(fieldId))?.name || 'Yeni Tarla';
                          const confirmMsg = `Sensör ${pendingSensorId} zaten "${oldFieldName}" tarlasına bağlı.\n\nYeni konumu "${newFieldName}" tarlasıyla ilişkilendirmek istediğinize emin misiniz?`;
                          if (!window.confirm(confirmMsg)) {
                            setIsAddingSensor(false);
                            setPendingSensorId(null);
                            return;
                          }
                        }
                        try {
                          setLoading(true);
                          await sensorService.updateSensorLocation(pendingSensorId, { lat, lng, fieldId });
                          await loadSensors();
                          alert(`Sensör ${pendingSensorId} konumu başarıyla güncellendi!`);
                        } catch (error) {
                          alert('Sensör güncellenirken hata: ' + error.message);
                        } finally {
                          setLoading(false);
                          setIsAddingSensor(false);
                          setPendingSensorId(null);
                        }
                      }
                    }}
                  />

                  {/* Sulama FAB — sürüklenebilir, tıklayınca panel */}
                  {selectedField && (
                    <DraggableFab
                      isActive={irrigationOn}
                      onToggle={() => setShowIrrPanel(v => !v)}
                      label={language === 'en' ? 'Irrigation Control' : 'Sulama Kontrolü'}
                      storageKey="swamed_fab_pos"
                    >
                      {showIrrPanel ? (
                        <IrrigationControl
                          field={selectedField}
                          isAdmin={isAdmin}
                          onIrrigationChange={handleIrrigationChange}
                          language={language}
                        />
                      ) : null}
                    </DraggableFab>
                  )}
                </div>

                {/* Timeline — haritanın altında sabit bar, scroll edilebilir */}
                {sentinelHistory && sentinelHistory.length > 0 && (
                  <div className="map-timeline-bar">
                    <MapTimeline
                      data={sentinelHistory}
                      onSelect={handleTimelineClick}
                      selectedDate={heatmapData?.date}
                      activeIndex={activeIndex}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'analysis' && selectedField && (
              <div className="analysis-panel">
                <NdviTimeline
                  fieldId={selectedField.id}
                  data={sentinelHistory}
                  onPointClick={handleTimelineClick}
                  activeIndex={activeIndex}
                />
              </div>
            )}
            {activeTab === 'risk' && isAdmin && (
              <div className="risk-panel">
                <DssCenter
                  fields={fields}
                  sensors={sensors}
                  token={localStorage.getItem('auth_token')}
                  language={language}
                />
              </div>
            )}

            {activeTab === 'users' && isAdmin && (
              <div className="users-panel">
                <UserManagement />
              </div>
            )}

            {activeTab === 'apikeys' && isAdmin && (
              <div className="users-panel">
                <ApiKeyManager onSensorsChanged={loadSensors} fieldId={selectedField?.id} fields={fields} />
              </div>
            )}

            {activeTab === 'backup' && isAdmin && (
              <div className="users-panel">
                <BackupManager />
              </div>
            )}

            {activeTab === 'audit' && isAdmin && (
              <div className="users-panel">
                <AuditLog />
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="profile-panel">
                <UserProfile
                  profile={profile}
                  onUpdate={(updated) => setProfile(updated)}
                  initialTab={profileSubTab}
                />
              </div>
            )}

            {activeTab === 'messages' && (
              <div className="messages-panel">
                <MessageCenter user={user} profile={profile} />
              </div>
            )}


          </div>
        </main >

        {/* Sağ Panel - Seçili Tarla Detayları */}
        {
          selectedField && (
            <aside className="info-panel">
              <div className="info-panel-header">
                <h3>{t('field_details.title')}</h3>
                <div className="field-type-tag">{selectedField.cropType || t('field_details.not_specified')}</div>
              </div>

              <div className="field-details-professional">
                <div className="detail-card main-info">
                  <div className="detail-row-modern">
                    <div className="row-icon"><Leaf size={16} /></div>
                    <div className="row-content">
                      <label>{t('field_details.field_name')}</label>
                      <span className="value-prominent">{selectedField.name}</span>
                    </div>
                  </div>

                  <div className="row-divider"></div>

                  <div className="detail-row-modern">
                    <div className="row-icon"><Map size={16} /></div>
                    <div className="row-content">
                      <label>{t('field_details.area_label')}</label>
                      <span className="value-standard">{selectedField.area} <small>{t('field_details.hectare')}</small></span>
                    </div>
                  </div>

                  <div className="row-divider"></div>

                  <div className="detail-row-modern">
                    <div className="row-icon"><Sun size={16} /></div>
                    <div className="row-content">
                      <label>{t('field_details.planting_date')}</label>
                      <span className="value-standard">
                        {selectedField.plantingDate
                          ? new Date(selectedField.plantingDate).toLocaleDateString(currentLocale, { day: '2-digit', month: 'long', year: 'numeric' })
                          : t('field_details.not_specified')}
                      </span>
                    </div>
                  </div>

                </div>

                {/* ── ET₀ / ETc Card ─────────────────────────────────── */}
                {et0Result && (
                  <div className="et0-card">
                    <div className="et0-header">
                      <span className="et0-title">FAO-56 Penman-Monteith</span>
                      {selectedField.elevation != null && (
                        <span className="et0-elev">⛰ {selectedField.elevation} m</span>
                      )}
                    </div>
                    <div className="et0-metrics">
                      <div className="et0-metric">
                        <span className="et0-label">ET₀</span>
                        <span className="et0-value">{et0Result.et0}<small> mm/day</small></span>
                      </div>
                      {et0Result.etc != null && (
                        <>
                          <div className="et0-divider"></div>
                          <div className="et0-metric">
                            <span className="et0-label">ETc <small>Kc {et0Result.kc}</small></span>
                            <span className="et0-value et0-etc">{et0Result.etc}<small> mm/day</small></span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="et0-crop-row">
                      <span className="et0-crop-name">{selectedField.cropType || '—'}</span>
                      <span className="et0-sensor-row" style={et0Result.stale ? { color: '#b45309' } : undefined}>
                        {et0Result.title}
                      </span>
                    </div>
                  </div>
                )}

                {heatmapData && (
                  <div className="detail-card health-info">
                    <h4><BarChart2 size={13} style={{verticalAlign:'middle', marginRight:5}} />{t('field_details.current_status')}</h4>

                    <div className="last-data-date">
                      <span>{t('field_details.last_data_date')}</span>
                      <span>{new Date(heatmapData.date).toLocaleDateString(currentLocale)}</span>
                    </div>

                    <div className="health-indices-container">
                      {/* NDVI Block */}
                      <div className="index-block">
                        <div className="index-header">
                          <span className="index-title">{t('field_details.avg_ndvi')}</span>
                          <span className="index-value-main" style={{ color: heatmapData.color }}>
                            {heatmapData.ndvi?.toFixed(3) || '0.000'}
                          </span>
                        </div>
                        <div className="index-stats-row">
                          <div className="index-stat-item">
                            <span className="index-stat-label">{t('field_details.min_value')}</span>
                            <span className="index-stat-value">{heatmapData.minNdvi?.toFixed(3) || '0.000'}</span>
                          </div>
                          <div className="index-stat-item" style={{ textAlign: 'right' }}>
                            <span className="index-stat-label">{t('field_details.max_value')}</span>
                            <span className="index-stat-value">{heatmapData.maxNdvi?.toFixed(3) || '0.000'}</span>
                          </div>
                        </div>
                      </div>

                      {/* NDMI Block */}
                      {heatmapData.ndmi !== undefined && (
                        <div className="index-block" style={{ borderLeft: '4px solid #00008B' }}>
                          <div className="index-header">
                            <span className="index-title">{t('field_details.avg_ndmi')}</span>
                            <span className="index-value-main" style={{ color: '#00008B' }}>
                              {heatmapData.ndmi?.toFixed(3) || '0.000'}
                            </span>
                          </div>
                          <div className="index-stats-row">
                            <div className="index-stat-item">
                              <span className="index-stat-label">{t('field_details.min_value')}</span>
                              <span className="index-stat-value">{heatmapData.minNdmi?.toFixed(3) || '0.000'}</span>
                            </div>
                            <div className="index-stat-item" style={{ textAlign: 'right' }}>
                              <span className="index-stat-label">{t('field_details.max_value')}</span>
                              <span className="index-stat-value">{heatmapData.maxNdmi?.toFixed(3) || '0.000'}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* NDRE Block */}
                      {heatmapData.ndre !== undefined && (
                        <div className="index-block" style={{ borderLeft: '4px solid #2c7a7b' }}>
                          <div className="index-header">
                            <span className="index-title">{t('field_details.avg_ndre')}</span>
                            <span className="index-value-main" style={{ color: '#2c7a7b' }}>
                              {heatmapData.ndre?.toFixed(3) || '0.000'}
                            </span>
                          </div>
                          <div className="index-stats-row">
                            <div className="index-stat-item">
                              <span className="index-stat-label">{t('field_details.min_value')}</span>
                              <span className="index-stat-value">{heatmapData.minNdre?.toFixed(3) || '0.000'}</span>
                            </div>
                            <div className="index-stat-item" style={{ textAlign: 'right' }}>
                              <span className="index-stat-label">{t('field_details.max_value')}</span>
                              <span className="index-stat-value">{heatmapData.maxNdre?.toFixed(3) || '0.000'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedField && (
                  <div className="detail-card weather-card">
                    <WeatherWidget
                      lat={selectedField.geometry.coordinates[0][0][1]}
                      lng={selectedField.geometry.coordinates[0][0][0]}
                    />
                  </div>
                )}

                {/* Görüntü Yenileme - only for ADMIN */}
                {isAdmin && (
                  <div className="panel-actions">
                    <button className="btn-secondary-outline" onClick={handleSearchImages}>
                      {t('dashboard.refresh_images')}
                    </button>
                  </div>
                )}

                {/* AI Advisor Button — admin only */}
                {isAdmin && heatmapData && (
                  <div className="panel-actions ai-advisor-section">
                    <button
                      className="btn-ai-advisor"
                      onClick={() => setShowAiAdvisor(true)}
                    >
                      {t('ai.open_advisor')}
                    </button>
                  </div>
                )}

                {/* Report Generator — admin only */}
                {isAdmin && (
                  <ReportGenerator
                    fieldId={selectedField.id}
                    userId={user.uid}
                    fieldName={selectedField.name}
                  />
                )}
              </div>
            </aside>
          )
        }
      </div >

      {/* Sensor Detail Modal */}
      {
        selectedSensor && (
          <SensorDetail
            sensor={selectedSensor}
            onClose={() => setSelectedSensor(null)}
          />
        )
      }

      {/* AI Advisor Modal */}
      {showAiAdvisor && selectedField && heatmapData && (
        <AiAdvisor
          field={selectedField}
          analysisData={heatmapData}
          onClose={() => setShowAiAdvisor(false)}
        />
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <button className={`mbn-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <span className="mbn-icon">📊</span>
          <span className="mbn-label">{language === 'en' ? 'Overview' : 'Özet'}</span>
        </button>
        <button className={`mbn-tab ${activeTab === 'map' ? 'active' : ''}`} onClick={() => setActiveTab('map')} disabled={!selectedField}>
          <span className="mbn-icon">🗺️</span>
          <span className="mbn-label">{language === 'en' ? 'Map' : 'Harita'}</span>
        </button>
        <button className={`mbn-tab ${activeTab === 'analysis' ? 'active' : ''}`} onClick={() => setActiveTab('analysis')} disabled={!selectedField}>
          <span className="mbn-icon">📈</span>
          <span className="mbn-label">{language === 'en' ? 'Analysis' : 'Analiz'}</span>
        </button>
        <button className={`mbn-tab ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')}>
          <span className="mbn-icon">💬</span>
          <span className="mbn-label">{language === 'en' ? 'Messages' : 'Mesaj'}</span>
          {unreadCount > 0 && <span className="mbn-badge">{unreadCount}</span>}
        </button>
        <button className={`mbn-tab ${showMobileDrawer ? 'active' : ''}`} onClick={() => setShowMobileDrawer(v => !v)}>
          <span className="mbn-icon">☰</span>
          <span className="mbn-label">{language === 'en' ? 'More' : 'Daha'}</span>
        </button>
      </nav>

      {/* Mobile Drawer — soldan açılan yan menü */}
      {showMobileDrawer && (
        <div className="mobile-drawer-overlay" onClick={() => setShowMobileDrawer(false)}>
          <div className="mobile-drawer" onClick={e => e.stopPropagation()}>
            {/* Drawer Header */}
            <div className="drawer-header">
              <div className="drawer-brand">
                <span className="drawer-brand-name">SWAMED</span>
                <span className="drawer-brand-role">{isAdmin ? (language === 'en' ? 'Admin' : 'Yönetici') : (language === 'en' ? 'User' : 'Kullanıcı')}</span>
              </div>
              <button className="drawer-close" onClick={() => setShowMobileDrawer(false)}>✕</button>
            </div>

            {/* Profil özeti */}
            <div className="drawer-profile">
              <div className="drawer-avatar">
                {(profile?.firstName || profile?.name || user?.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="drawer-profile-info">
                <span className="drawer-profile-name">
                  {profile?.firstName ? `${profile.firstName} ${profile.lastName}` : (profile?.name || user?.email || 'Kullanıcı')}
                </span>
                <span className="drawer-profile-email">{profile?.email}</span>
              </div>
            </div>

            {/* Menü bölümleri */}
            <div className="drawer-nav">
              {/* Profil */}
              <button className={`drawer-item ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => { setActiveTab('profile'); setShowMobileDrawer(false); }}>
                <span className="drawer-item-icon">👤</span>
                <span className="drawer-item-label">{t('sidebar.profile')}</span>
              </button>

              {/* Yeni tarla (admin) */}
              {isAdmin && (
                <>
                  <div className="drawer-section-label">{language === 'en' ? 'Admin' : 'Yönetim'}</div>

                  <button className="drawer-item"
                    onClick={() => {
                      setSelectedField(null);
                      setActiveTab('map');
                      setShowMobileDrawer(false);
                      setTimeout(() => alert(`${t('dashboard.mapMode.title')}\n\n${t('dashboard.mapMode.instructions')}`), 100);
                    }}>
                    <span className="drawer-item-icon">➕</span>
                    <span className="drawer-item-label">{t('header.new_field')}</span>
                  </button>

                  <button className={`drawer-item ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('users'); setShowMobileDrawer(false); }}>
                    <span className="drawer-item-icon">👥</span>
                    <span className="drawer-item-label">{t('sidebar.user_management')}</span>
                  </button>

                  <button className={`drawer-item ${activeTab === 'apikeys' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('apikeys'); setShowMobileDrawer(false); }}>
                    <span className="drawer-item-icon">🔑</span>
                    <span className="drawer-item-label">API {language === 'en' ? 'Integration' : 'Entegrasyonu'}</span>
                  </button>

                  <button className={`drawer-item ${activeTab === 'risk' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('risk'); setShowMobileDrawer(false); }}>
                    <span className="drawer-item-icon">🧩</span>
                    <span className="drawer-item-label">Decision Support</span>
                  </button>

                  <button className={`drawer-item ${activeTab === 'backup' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('backup'); setShowMobileDrawer(false); }}>
                    <span className="drawer-item-icon">💾</span>
                    <span className="drawer-item-label">Backup</span>
                  </button>

                  <button className={`drawer-item ${activeTab === 'audit' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('audit'); setShowMobileDrawer(false); }}>
                    <span className="drawer-item-icon">📋</span>
                    <span className="drawer-item-label">Audit Log</span>
                  </button>
                </>
              )}

              <div className="drawer-divider" />

              {/* Dil değiştir */}
              <div className="drawer-section-label">{language === 'en' ? 'Settings' : 'Ayarlar'}</div>
              <div className="drawer-item drawer-item-lang">
                <span className="drawer-item-icon">🌐</span>
                <span className="drawer-item-label">{language === 'en' ? 'Language' : 'Dil'}</span>
                <LanguageSwitcher />
              </div>

              <div className="drawer-divider" />

              <button className="drawer-item drawer-item-logout" onClick={() => { handleLogout(); setShowMobileDrawer(false); }}>
                <span className="drawer-item-icon">🚪</span>
                <span className="drawer-item-label">{t('header.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {
        loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>{t('loadingOverlay.processing')}</p>
          </div>
        )
      }
      {/* Sensör Seçme Modalı */}
      {
        isSensorModalOpen && (
          <div className="sensor-select-modal">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h3>📡 {t('sensorModal.title')}</h3>
                <button
                  className="close-btn"
                  onClick={() => {
                    setIsSensorModalOpen(false);
                    setSelectedSensorIds([]);
                    setBatchSensorMode(false);
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                {/* Mode Toggle */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <button
                    className={`btn-toggle ${!batchSensorMode ? 'active' : ''}`}
                    onClick={() => {
                      setBatchSensorMode(false);
                      setSelectedSensorIds([]);
                    }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: !batchSensorMode ? '2px solid #6366f1' : '1px solid #e2e8f0',
                      background: !batchSensorMode ? '#eef2ff' : '#fff',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: !batchSensorMode ? '#4f46e5' : '#64748b'
                    }}
                  >
                    📍 {t('sensorModal.singleSensor')}
                  </button>
                  <button
                    className={`btn-toggle ${batchSensorMode ? 'active' : ''}`}
                    onClick={() => setBatchSensorMode(true)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: batchSensorMode ? '2px solid #6366f1' : '1px solid #e2e8f0',
                      background: batchSensorMode ? '#eef2ff' : '#fff',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: batchSensorMode ? '#4f46e5' : '#64748b'
                    }}
                  >
                    📡 {t('sensorModal.batchAdd')}
                  </button>
                </div>

                {!batchSensorMode ? (
                  <>
                    <p>{t('sensorModal.selectSensorToUpdate')}</p>
                    <div className="sensor-selection-wrapper">
                      <select
                        className="sensor-select-dropdown"
                        onChange={(e) => {
                          if (e.target.value) {
                            selectSensorForPlacement(e.target.value);
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="" disabled>{t('sensorModal.selectSensorPlaceholder')}</option>
                        {availableSensorIds.sort().map(id => (
                          <option key={id} value={id}>📡 Sensör {id}</option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      <strong>{t('sensorModal.targetField')}</strong>{' '}
                      {selectedField ? (
                        <span style={{ color: '#6366f1', fontWeight: 600 }}>{selectedField.name}</span>
                      ) : (
                        <span style={{ color: '#f43f5e' }}>{t('sensorModal.selectFieldFirst')}</span>
                      )}
                    </p>
                    <p style={{ marginBottom: '10px', color: '#64748b', fontSize: '0.9rem' }}>
                      {t('sensorModal.selectSensorsToAdd')}
                    </p>
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '8px'
                    }}>
                      {availableSensorIds.sort().map(id => (
                        <label
                          key={id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            background: selectedSensorIds.includes(id) ? '#eef2ff' : 'transparent',
                            marginBottom: '4px'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSensorIds.includes(id)}
                            onChange={() => toggleSensorSelection(id)}
                            style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                          />
                          📡 Sensör {id}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => setSelectedSensorIds([...availableSensorIds])}
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '1px solid #6366f1',
                          background: '#fff',
                          color: '#6366f1',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        {t('sensorModal.selectAll')}
                      </button>
                      <button
                        onClick={() => setSelectedSensorIds([])}
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '1px solid #e2e8f0',
                          background: '#fff',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        {t('sensorModal.clear')}
                      </button>
                    </div>
                    <button
                      onClick={handleBatchSensorAdd}
                      disabled={selectedSensorIds.length === 0 || !selectedField}
                      style={{
                        marginTop: '15px',
                        width: '100%',
                        padding: '12px',
                        background: selectedSensorIds.length > 0 && selectedField ? '#6366f1' : '#cbd5e1',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        cursor: selectedSensorIds.length > 0 && selectedField ? 'pointer' : 'not-allowed'
                      }}
                    >
                      ✅ {selectedSensorIds.length} {t('sensorModal.addToField')}
                    </button>
                  </>
                )}

                {availableSensorIds.length === 0 && (
                  <p className="no-data">{t('sensorModal.noSensorsAvailable')}</p>
                )}
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Dashboard;
